/**
 * Meta (Facebook) pixel.
 *
 * The marketing site at autoinvent.com carries the same pixel, but it can only ever see a
 * visit. Everything worth optimising an ad against — an account being created, a trial
 * starting, a filing being paid for — happens here, on autoinvent.app, which is a different
 * domain and a different codebase. Until this existed, a campaign could bid for landing-page
 * traffic and nothing more.
 *
 * Three things about this file are deliberate:
 *
 * 1. It is loaded from JS, gated on VITE_META_PIXEL_ID, rather than pasted into index.html.
 *    That is how Sentry and PostHog are already wired in main.jsx, and it means a preview
 *    deploy or a local dev server without the variable reports nothing instead of polluting
 *    production ad data with test traffic.
 *
 * 2. Nothing here throws. Analytics must never be able to take a page down — the same rule
 *    analytics.js and services/analytics.service.ts hold to.
 *
 * 3. init() does NOT fire the initial PageView. This is a single-page app, so the browser
 *    loads one document per session; Meta's copy-paste snippet would therefore report one
 *    pageview and miss every route change after it. App.jsx owns pageviews instead, firing
 *    on every location change including the first. (main.jsx makes the same correction for
 *    PostHog with capture_pageview: 'history_change'.)
 *
 * The fbclid ad-click id needs no handling here. The marketing site appends it to app links
 * because .com and .app cannot share a cookie; the pixel reads it off the URL at init and
 * writes its own _fbc, and versionedUrl() in utils/versionReload.js deliberately preserves
 * the query string, so it survives a recovery reload on the way in.
 */

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID || '';

/** The one-time filing fee. Mirrors FILING_FEE_LINE in utils/planFeatures.js. */
const FILING_FEE_USD = 299;

/** Meta's base code, verbatim apart from formatting. Defines window.fbq and loads the library. */
function loadBaseCode() {
  /* eslint-disable */
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
}

/** Load the pixel and identify it. Safe to call more than once; the base code guards itself. */
export function initMetaPixel() {
  if (!PIXEL_ID) return;
  try {
    loadBaseCode();
    window.fbq('init', PIXEL_ID);
  } catch (err) {
    console.warn('[meta-pixel] init failed:', err?.message || err);
  }
}

/** Send one event. A no-op when the pixel was never configured or failed to load. */
function track(event, properties, options) {
  if (!PIXEL_ID || typeof window.fbq !== 'function') return;
  try {
    window.fbq('track', event, properties, options);
  } catch (err) {
    console.warn(`[meta-pixel] failed to send ${event}:`, err?.message || err);
  }
}

/**
 * Run `fn` at most once per browser, for a given key.
 *
 * Both conversion events below sit in effects that can run again — PaymentSuccess re-runs its
 * effect once `user` resolves, and either success page survives a refresh with the same URL.
 * Re-firing would report a second sale that never happened and inflate the return Meta thinks
 * the campaign is producing. localStorage rather than a ref, because a refresh clears a ref.
 */
function fireOnce(key, fn) {
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, '1');
  } catch {
    // Private mode: no memory of a previous fire. Sending a possible duplicate beats
    // silently dropping a real conversion.
  }
  fn();
}

/**
 * Tie this browser's events to a known person.
 *
 * `external_id` is the Firebase uid, the same id PostHog identifies on and the same one the
 * Conversions API sends hashed from the server (services/meta-capi.service.ts). Matching ids
 * on both sides is what lets Meta recognise a webhook three days later as the person who
 * clicked the ad, rather than as a stranger. The pixel hashes the value itself before it
 * leaves the browser.
 */
export function setMetaUser(uid) {
  if (!PIXEL_ID || !uid || typeof window.fbq !== 'function') return;
  try {
    window.fbq('init', PIXEL_ID, { external_id: uid });
  } catch (err) {
    console.warn('[meta-pixel] identify failed:', err?.message || err);
  }
}

/**
 * The pixel's own attribution cookies, read so the server can send them later.
 *
 * `_fbc` holds the ad click; it is the strongest match key Meta accepts and the one that ties
 * a payment back to a campaign. Both cookies live on this domain only, so a Stripe webhook —
 * a different process, days later, with no browser anywhere near it — can never read them.
 * AuthContext copies whatever is here onto the user document for that reason.
 */
export function readMetaCookies() {
  try {
    const read = (name) =>
      document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${name}=`))
        ?.split('=')[1] || null;
    return { fbc: read('_fbc'), fbp: read('_fbp') };
  } catch {
    return { fbc: null, fbp: null };
  }
}

/** One pageview per route. Called from App.jsx on every location change. */
export function trackPageView() {
  track('PageView');
}

/**
 * A new account exists.
 *
 * Fired from createUserProfile in AuthContext, inside the branch that actually writes the
 * document — not from the signup form. Both routes to a new account converge there (the
 * email form calls it directly, a first Google sign-in reaches it through
 * ensureUserProfile), so this covers the Google popup AND redirect paths, and it cannot fire
 * for a returning user signing in again.
 */
export function trackSignupCompleted(properties = {}) {
  track('CompleteRegistration', { status: true, ...properties });
}

/**
 * Checkout completed on a subscription.
 *
 * StartTrial, not Purchase: every plan is sold with a three-day trial (see the comments in
 * modules/stripe/handlers/subscription.handler.ts), so nothing has been charged at this
 * point. Reporting a Purchase here would book revenue that does not exist yet and train the
 * campaign to find people who start trials and cancel on day three.
 *
 * No value either, for the same reason. Real subscription revenue arrives at
 * invoice.payment_succeeded in the Stripe webhook, hours or days later, where only a
 * server-side Conversions API call can report it.
 */
export function trackTrialStarted({ sessionId, plan }) {
  fireOnce(`metaPixel:trial:${sessionId || 'unknown'}`, () =>
    track('StartTrial', plan ? { plan } : {}),
  );
}

/**
 * A filing was paid for — the one place in the web app where money genuinely moves at the
 * moment the browser is watching, so the one place Purchase belongs.
 */
export function trackFilingPurchase({ patentId }) {
  fireOnce(`metaPixel:purchase:${patentId}`, () =>
    track(
      'Purchase',
      {
        value: FILING_FEE_USD,
        currency: 'USD',
        content_type: 'product',
        content_name: 'Patent filing',
        content_ids: [patentId],
      },
      /*
       * The Stripe webhook sends this same sale from the server, where an ad blocker cannot
       * reach it. `eventID` is what tells Meta the two are one event: it must match the id
       * built in payment.handler.ts exactly, or the $320 is booked twice.
       */
      { eventID: `filing_${patentId}` },
    ),
  );
}
